/**
 * The customer mail in the viewer's language.
 *
 * `GET /cases/{id}/translation?target=` answers without a model call: the stored
 * translation, or the mail as it is when it is already in the target language (and
 * `translation: null` when it still needs translating). Only then is
 * `POST /cases/{id}/translation {target}` asked, which makes the translation. So an
 * English mail opened in English sends GETs only. Nothing changes the stored mail and
 * nothing leaves the app.
 *
 * Kept free of React so the request shape and the wording can be tested.
 */

import { logger } from "@/lib/logger";

import type { ApiError } from "./client";
import {
  type CaseTranslation,
  type CaseTranslationRequest,
  getCaseTranslation as getCaseTranslationSdk,
  translateCaseMail as translateCaseMailSdk,
} from "./generated";

/** The languages a viewer can read the mail in (`en`, `da`, `de`, `fr`, `es`, `cs`). */
export type TranslationTarget = CaseTranslationRequest["target"];

/** Ask for the translation of a case mail into `target`. Throws an {@link ApiError}. */
export async function translateCaseMail(
  caseId: string,
  target: TranslationTarget,
): Promise<CaseTranslation> {
  const { data } = await translateCaseMailSdk({
    path: { case_id: caseId },
    body: { target },
    throwOnError: true,
  });
  return data;
}

/**
 * The stored translation (or the same-language answer) without a model call; null when
 * the mail still needs translating. Throws an {@link ApiError}.
 */
export async function readCaseMailTranslation(
  caseId: string,
  target: TranslationTarget,
): Promise<CaseTranslation | null> {
  const { data } = await getCaseTranslationSdk({
    path: { case_id: caseId },
    query: { target },
    throwOnError: true,
  });
  return data?.translation ?? null;
}

/**
 * How long a failed translation is kept before the model is asked again for the same
 * case and language (ease round, 13 Oct 2026). The query of the page asks once and does
 * not ask again when the case opens again. One path still asked again: the query turned
 * off and on while it was on the page (the viewer switches the language away and back, or
 * the case changes scope). This guard closes that path for the busy service and for the
 * hourly limit alike.
 */
export const FAILED_TRANSLATION_KEEP_MS = 5 * 60_000;

/** The failed translations of this visit: "case|language" with the error and its time. */
const failedTranslations = new Map<string, { error: unknown; at: number }>();

/** The remembered failure of a case and language that is still kept at `now`, or null. */
export function keptTranslationFailure(
  caseId: string,
  target: TranslationTarget,
  now: number = Date.now(),
): { error: unknown; at: number } | null {
  const key = `${caseId}|${target}`;
  const kept = failedTranslations.get(key);
  if (!kept) {
    return null;
  }
  if (now - kept.at >= FAILED_TRANSLATION_KEEP_MS) {
    failedTranslations.delete(key);
    return null;
  }
  return kept;
}

/** Forget every remembered failure (used by the tests; a reload of the page forgets them too). */
export function forgetTranslationFailures(): void {
  failedTranslations.clear();
}

/**
 * The case mail in `target`: the GET answer when it has one (cached, same language, no
 * mail), else one POST that translates. A backend without the GET (405) is asked by POST.
 * A POST that failed is not sent again for `FAILED_TRANSLATION_KEEP_MS`: the same error
 * is thrown without a request, and the page keeps the original with its one sentence.
 */
export async function loadCaseTranslation(
  caseId: string,
  target: TranslationTarget,
): Promise<CaseTranslation> {
  try {
    const stored = await readCaseMailTranslation(caseId, target);
    if (stored) {
      logger.debug("[translation] answered by GET", { caseId, target, status: stored.status });
      return stored;
    }
  } catch (error) {
    if ((error as Partial<ApiError> | null)?.status !== 405) {
      throw error;
    }
  }
  const kept = keptTranslationFailure(caseId, target);
  if (kept) {
    logger.debug("[translation] failed before, not asked again", { caseId, target });
    throw kept.error;
  }
  logger.debug("[translation] translating by POST", { caseId, target });
  try {
    return await translateCaseMail(caseId, target);
  } catch (error) {
    failedTranslations.set(`${caseId}|${target}`, { error, at: Date.now() });
    throw error;
  }
}

/** Why the original is shown instead of a translation. Raw server text never reaches the page. */
export function translationFailureText(error: unknown): string {
  const status = (error as Partial<ApiError> | null)?.status;
  if (status === 429) {
    return "You asked for many translations in the last hour, so the original is shown.";
  }
  if (status === 503) {
    return "Translation is not available right now, so the original is shown.";
  }
  return "The mail could not be translated, so the original is shown.";
}

/** The sentence for a translation the model could not make. */
export const TRANSLATION_UNAVAILABLE_TEXT =
  "This mail could not be translated, so the original is shown.";

/** The English name of a language code ("es" reads "Spanish"); the code itself when unknown. */
export function languageName(code: string | null | undefined): string | null {
  const value = (code ?? "").trim();
  if (value === "") {
    return null;
  }
  try {
    const name = new Intl.DisplayNames(["en"], { type: "language", fallback: "none" }).of(value);
    if (name) {
      return name;
    }
  } catch {
    // An invalid code: show it as it came.
  }
  return value.toUpperCase();
}
